import { useEffect, useRef, useState } from "react";
import { truncate } from "lodash";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon } from "@circle-react-shared/Icon";
import { useToast } from "@circle-react-shared/uikit/ToastV2";

interface TranscriptFileUploadInputComponentProps {
  errorMessage: string;
  name: string;
  onChange: (value: string) => void;
  placeholder: string;
}

export const TranscriptFileUploadInputComponent = ({
  name,
  placeholder,
  errorMessage,
  onChange,
}: TranscriptFileUploadInputComponentProps) => {
  const { error } = useToast();
  const [fileName, setFileName] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (errorMessage) {
      error(errorMessage);
    }
  }, [errorMessage]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Read as data URL
    const reader = new FileReader();
    reader.onload = () => {
      onChange(reader.result as string);
    };
    reader.readAsDataURL(file);

    setFileName(file.name);
  };

  const handleRemoveFile = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setFileName(null);
    onChange("");
  };

  return (
    <div>
      <label
        className={classNames(
          "bg-primary flex cursor-pointer rounded-md border border-gray-600 px-2 py-1.5 text-sm",
          {
            "hover:bg-selector-active justify-center": !fileName,
            "border-red-500": errorMessage,
          },
        )}
      >
        {!fileName && <span>{placeholder}</span>}
        {fileName && (
          <span className="flex gap-2">
            <Icon type="20-attach" size={20} className="shrink-0" />
            <span className="grow">
              {truncate(fileName, { length: 17, omission: "..." })}
            </span>
            <Icon
              type="16-close"
              size={16}
              className="shrink-0"
              onClick={handleRemoveFile}
            />
          </span>
        )}
        <input
          className="hidden"
          type="file"
          name={name}
          itemType="text/vtt"
          accept=".vtt"
          onChange={handleOnChange}
          ref={inputRef}
        />
      </label>
    </div>
  );
};
